import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { useQuery } from "@tanstack/react-query";
import { Music, Disc } from "react-feather";

import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import {
  Button,
  CircularProgress,
  CssBaseline,
  Grid,
  ScopedCssBaseline,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import theme from "./theme/theme";

import SearchField from "./components/SearchField";
import useDebounce from "./hooks/useDebounce";
import { LoadingButton } from "@mui/lab";
import { inherits } from "util";
import glassyBackground from "./theme/glassyBackground";
interface Song {
  title: string;
  artists: string;
  album: string;
}

interface TrackSearchResult {
  total: number;
  songs: Song[];
}

function App() {
  const [config, setConfig] = useState({
    subtitle: "Probier hier aus ob dein Lied auf der Jukebox verfügbar ist!",
    title: "Ist dein Lied dabei?",
    cta: "Gib einen Song ein und klicke hier",
    error: "Bitte werde etwas genauer, oder hast du dich vertippt?",
    arrowSrc:
      "https://jukebox-3000.de/wp-content/uploads/2024/01/Curly-Arrow-PNG.png",
  });

  useEffect(() => {
    const appRoot = document.getElementById(
      "jukebox-music-search"
    ) as HTMLElement;

    // @ts-ignore
    setConfig({ ...config, ...appRoot.dataset });
  }, []);

  const isSmall = useMediaQuery(theme.breakpoints.up("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const numberOfItems = useMemo(() => {
    return isLarge ? 24 : isMedium ? 16 : isSmall ? 12 : 6;
  }, [isSmall, isMedium, isLarge]);

  const [hasSearched, setHasSearched] = useState(false);
  const [songSearchValue, setSongSearchValue] = useState("");
  const [songs, setSongs] = useState<Song[]>([]);
  const [otherAvailableSongs, setOtherAvailableSongs] = useState(0);

  const [loading, setLoading] = useState(false);

  const searchSongs = (searchTerm: string) => {
    setSongs([]);
    setOtherAvailableSongs(0);
    setLoading(true);

    fetch(
      `https://app.jukebox-3000.de/api/public/music_availability?q=${searchTerm}&numberOfItems=${numberOfItems}`
    )
      .then((res) => res.json() as Promise<TrackSearchResult>)
      .then((searchResults) => {
        setSongs(searchResults.songs);
        setOtherAvailableSongs(searchResults.total);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const isValueValid = useMemo(() => {
    return songs !== undefined && songs !== null && songs.length > 0;
  }, [songs]);

  return (
    <ThemeProvider theme={theme}>
      <Stack
        sx={{ background: "none", p: 2, flexGrow: 2 }}
        alignItems="center"
        gap={4}
        maxWidth={800}
      >
        <Stack alignItems="center">
          <Typography
            textAlign="center"
            variant="h3"
            className="jukebox-music-search--title"
          >
            {config.title}
          </Typography>
          <Typography
            textAlign="center"
            className="jukebox-music-search--subtitle"
          >
            <b>{config.subtitle}</b>
          </Typography>
        </Stack>
        <Stack
          sx={{
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
          gap={2}
          width="100%"
        >
          <SearchField
            error={!isValueValid && hasSearched}
            onClearClick={() => {
              setSongSearchValue("");
              setHasSearched(false);
              setSongs([]);
              setOtherAvailableSongs(0);
            }}
            disabled={loading}
            onSubmit={() => {
              searchSongs(songSearchValue);
            }}
            sx={{ flexGrow: 2 }}
            placeholder="Songname eingeben..."
            onSearchChange={(search) => {
              setSongSearchValue(search);
              setHasSearched(false);
              setSongs([]);
              setOtherAvailableSongs(0);
            }}
            search={songSearchValue}
          />
          <LoadingButton
            loading={loading}
            disabled={songSearchValue.length <= 0}
            onClick={() => {
              searchSongs(songSearchValue);
            }}
            variant="contained"
            sx={{ px: 2 }}
          >
            <span style={{ padding: "0 16px" }}>Überprüfen</span>
          </LoadingButton>
        </Stack>

        {loading ? (
          <Stack alignItems="center" gap={1}>
            <Typography className="jukebox-music-search--search-note">
              Wir durchsuchen unsere Bibliothek
            </Typography>
            <CircularProgress size={60} />
          </Stack>
        ) : otherAvailableSongs > 0 ? (
          <>
            <Grid
              container
              spacing={2}
              className="jukebox-music-search--result-list"
              ref={(el) => {
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <Grid xs={12} pb={2} sx={{ opacity: 0.5 }}>
                <Typography className="jukebox-music-search--results-title">
                  Diese Songs haben wir auf der Jukebox gefunden:
                </Typography>
              </Grid>
              {songs.map((song) => (
                <Grid item xs={12} md={6}>
                  <Stack
                    sx={{
                      ...glassyBackground,
                      px: 1.5,
                      py: 2,
                      boxShadow: "0 2px 3px rgba(0,0,0,0.3)",
                    }}
                    className="jukebox-music-search--results-item"
                  >
                    <Stack direction="row" gap={1}>
                      <Disc style={{ flexShrink: 0 }} />
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          lineClamp: "1",
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-all",
                        }}
                      >
                        {song.title}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          lineClamp: "1",
                          WebkitBoxOrient: "vertical",
                          maxWidth: "50%",
                          wordBreak: "break-all",
                        }}
                        variant="caption"
                      >
                        {song.album}
                      </Typography>
                      <Typography>·</Typography>
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          lineClamp: "1",
                          WebkitBoxOrient: "vertical",
                          maxWidth: "50%",
                          wordBreak: "break-all",
                        }}
                        variant="caption"
                      >
                        {song.artists}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            <Stack
              alignItems="center"
              sx={{ color: theme.palette.primary.main }}
            >
              <Music size={80} />
              <Typography sx={{ color: "inherit" }} variant="h3">
                Und {otherAvailableSongs} weitere songs...
              </Typography>
            </Stack>
          </>
        ) : !hasSearched ? (
          <Stack
            width="100%"
            sx={{
              mt: -6,
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "flex-end",
              pr: 10,
              columnGap: 4,
              display: {
                md: "flex",
                xs: "none",
              },
            }}
          >
            <Typography>{config.cta}</Typography>
            <img
              style={{ width: 100, transform: "rotate(210deg)" }}
              src={config.arrowSrc}
            />
          </Stack>
        ) : (
          <Stack flexDirection="row" gap={1} alignItems="center">
            <span style={{ fontSize: 50 }}>🤔</span>
            <Typography color="error">{config.error}</Typography>
          </Stack>
        )}
      </Stack>
    </ThemeProvider>
  );
}

export default App;

// "homepage": "/wp-content/jukebox-music-search",
